import apiClient from '../../../shared/utils/axios';
import type { CampaignByIdResponse, CampaignScreenEditCampaign } from './types';

export interface CampaignsResult {
  headers: any[];
  rows: any[];
  totalItems: number;
  page: number;
  rowsPerPage: number;
}

export interface CampaignFilterDef {
  key: string;
  label: string;
  values?: { key: string; label: string }[];
}

export interface CampaignQueryParams {
  filters?: Record<string, any>;
  search?: string;
  page?: number;
  rowsPerPage?: number;
}

interface CampaignListData {
  headers: unknown[];
  rows: unknown[];
  total: number;
}

class CampaignAPI {
  async fetchCampaigns(params: CampaignQueryParams): Promise<CampaignsResult> {
    const {
      filters = {},
      search = '',
      page = 0,
      rowsPerPage = 10,
    } = params || {};

    // Format filters similar to your Redux action
    const formattedSelectedFilters = Object.entries(filters)
      .filter(([key, filter]) => !(key === 'state' && filter.value === 'ALL'))
      .map(([_, filter]) => {
        const { values, value, ...rest } = filter;
        return {
          ...rest,
          value: Array.isArray(value) ? value : [value],
        };
      });

    // Build URL with query parameters
    let url = `/dooh_api/campaign/v1/list?limit=${rowsPerPage}&offset=${page * rowsPerPage}`;

    if (search && search.length > 2) {
      url += `&searchText=${encodeURIComponent(search)}`;
    }

    // Use POST with filters in body (matching your original logic)
    const response = await apiClient.post<CampaignListData>(url, {
      filters: formattedSelectedFilters,
      sort: [],
    });

    // Transform response to match expected format
    const data = response?.data;
    return {
      headers: data?.headers || [],
      rows: data?.rows || [],
      totalItems: data?.total || 0,
      page,
      rowsPerPage,
    };
  }

  async fetchCampaignFilters(): Promise<CampaignFilterDef[]> {
    const response = await apiClient.post<CampaignFilterDef[]>(
      `/dooh_api/campaign/v1/filters`,
      {}
    );
    return response?.data || [];
  }

  // Advertiser Campaign API methods
  async createCampaign(payload: any): Promise<any> {
    try {
      return await apiClient.post('/dooh_api/campaign/v1/create', payload);
    } catch (error) {
      console.error('Error creating advertiser campaign:', error);
      throw error;
    }
  }

  async getCampaignById(id: string): Promise<CampaignByIdResponse> {
    return apiClient.get<CampaignScreenEditCampaign>(
      `/dooh_api/campaign/v1/${id}`
    );
  }

  async updateCampaign(campaignId: string, payload: any): Promise<any> {
    try {
      return await apiClient.put(
        `/dooh_api/campaign/v1/${campaignId}`,
        payload
      );
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  }

  // Delete campaign (soft delete via status update)
  async deleteCampaign(campaignId: string): Promise<any> {
    try {
      const response = await apiClient.post(
        `/dooh_api/campaign/v1/${campaignId}/status`,
        {
          status: 'DELETED',
        }
      );
      return response;
    } catch (error) {
      console.error('Error deleting campaign:', error);
      throw error;
    }
  }

  // Advertiser Campaign Filter APIs
  async fetchAdvertiserFilters(filters: any[] = []): Promise<any> {
    try {
      const response = await apiClient.post(
        '/dooh_api/screen/v1/advertiser/devices/filters',
        {
          filters,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching advertiser filters:', error);
      throw error;
    }
  }

  // Update campaign status (approve/reject)
  async setCampaignStatus(data: any, campaignId: string): Promise<any> {
    try {
      const response = await apiClient.post(
        `/dooh_api/campaign/v1/${campaignId}/approve`,
        data
      );
      return response;
    } catch (error) {
      console.error('Error updating campaign status:', error);
      throw error;
    }
  }

  async updateCampaignStatus(campaignId: string, status: string): Promise<any> {
    try {
      const response = await apiClient.post(
        `/dooh_api/campaign/v1/${campaignId}/status`,
        { status }
      );
      return response;
    } catch (error) {
      console.error('Error updating campaign status:', error);
      throw error;
    }
  }

  //  NEW: Fetch touchpoints with filters
  async fetchTouchpoints(payload: {
    filters: any[];
    sort: any[];
  }): Promise<any> {
    try {
      const response = await apiClient.post(
        '/dooh_api/touchpoint/v1/list',
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching touchpoints:', error);
      throw error;
    }
  }

  //  NEW: Fetch available filters
  async fetchTouchpointFilters(payload: {
    filters: any[];
    sort: any[];
  }): Promise<any> {
    try {
      const response = await apiClient.post(
        '/dooh_api/touchpoint/v1/filters',
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching filters:', error);
      throw error;
    }
  }
}

export const campaignAPI = new CampaignAPI();
export default campaignAPI;
